import { Spacer } from "@hackthenorth/north";
import { Label } from "north.js";
import React from "react";

import {
  PerkStatus,
  SponsorPerksData,
  SponsorPerkType,
} from "src/views/sponsor/perks/types";

import {
  PerkContainer,
  PerkTextInput,
  PerkTextArea,
  PerkError,
  PerkDatePicker,
} from "../components";
import Header from "../Header";
import { perkStatusToBadgeColor } from "../helpers";
import { TextDescription } from "../TextComponents";
import { PerkData } from "../usePerkState";

export const SPONSOR_A_MEETUP_VALIDATION_FIELDS: (keyof SponsorPerksData)[] = [
  "sponsor_a_meetup_name",
  "sponsor_a_meetup_description",
  "sponsor_a_meetup_time_one",
  "sponsor_a_meetup_time_two",
  "sponsor_a_meetup_time_three",
];

export const SponsorAMeetup: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const meetupDate1 = getState("sponsor_a_meetup_time_one");
  const meetupDate2 = getState("sponsor_a_meetup_time_two");
  const meetupDate3 = getState("sponsor_a_meetup_time_three");

  const meetupDateError = (meetupDate1 === meetupDate2 ||
    meetupDate1 === meetupDate3 ||
    meetupDate2 === meetupDate3) as boolean;

  return (
    <>
      <Header
        title="Sponsor a Meetup"
        subtitle="This perk is for a 1 hour meetup event with a hacker demographic of your choosing during the weekend of Hack the North."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              (getState("sponsor_a_meetup_status") ?? []) as PerkStatus
            )}
            size="sm"
          >
            {getState("sponsor_a_meetup_status")}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.SPONSOR_A_MEETUP}
        readOnly={isReadOnly}
      >
        <PerkTextInput
          title="Title*"
          value={getState("sponsor_a_meetup_name") ?? ""}
          placeholder="Your meetup title"
          isReadOnly={isReadOnly}
          onChange={(e) => updateState("sponsor_a_meetup_name", e.target.value)}
          error={error?.sponsor_a_meetup_name && <>This field is required</>}
        />
        <Spacer height={32} />
        <PerkTextArea
          title="Description* (max 100 words)"
          value={getState("sponsor_a_meetup_description") ?? ""}
          placeholder="Description of your meetup"
          wordLimit={100}
          isReadOnly={isReadOnly}
          onChange={(e) =>
            updateState("sponsor_a_meetup_description", e.target.value)
          }
          error={
            error?.sponsor_a_meetup_description && <>This field is required</>
          }
        />
        <Spacer height={32} />
        {meetupDateError && (
          <PerkError
            error={<>Make sure all your time selections are different</>}
          />
        )}
        <Spacer height={8} />
        <PerkDatePicker
          title="Meetup Time 1*"
          value={getState("sponsor_a_meetup_time_one") ?? "YYYY-MM-DD"}
          onChange={(date) => {
            if (date) updateState("sponsor_a_meetup_time_one", date);
          }}
          isReadOnly={isReadOnly}
          error={
            meetupDateError && error?.sponsor_a_meetup_time_one ? (
              <>You have duplicate time selections</>
            ) : (
              error?.sponsor_a_meetup_time_one &&
              !meetupDate3 && <>This field is required</>
            )
          }
        />
        <Spacer height={32} />
        <PerkDatePicker
          title="Meetup Time 2*"
          value={getState("sponsor_a_meetup_time_two") ?? "YYYY-MM-DD"}
          onChange={(date) => {
            if (date) updateState("sponsor_a_meetup_time_two", date);
          }}
          isReadOnly={isReadOnly}
          error={
            meetupDateError && error?.sponsor_a_meetup_time_two ? (
              <>You have duplicate time selections</>
            ) : (
              error?.sponsor_a_meetup_time_two &&
              !meetupDate3 && <>This field is required</>
            )
          }
        />
        <Spacer height={32} />
        <PerkDatePicker
          title="Meetup Time 3*"
          value={getState("sponsor_a_meetup_time_three") ?? "YYYY-MM-DD"}
          onChange={(date) => {
            if (date) updateState("sponsor_a_meetup_time_three", date);
          }}
          isReadOnly={isReadOnly}
          error={
            meetupDateError && error?.sponsor_a_meetup_time_three ? (
              <>You have duplicate time selections</>
            ) : (
              error?.sponsor_a_meetup_time_three &&
              !meetupDate3 && <>This field is required</>
            )
          }
        />
      </PerkContainer>
    </>
  );
};
const Description = () => (
  <>
    <TextDescription>
      This is a great opportunity to connect with hackers, share what your
      company is building, highlight your team culture and build real
      relationships with the future of tech talent.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      Please provide a description of your planned meetup for our event
      schedule, as well as a preferred start time. Once this perk has been moved
      to the Complete section, your time will be finalized.
    </TextDescription>
  </>
);
